import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Upload, Trash2, CalendarClock, Loader2, AlertTriangle, ImageOff } from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import AuthImage from '../media/AuthImage';
import { checkImageFile, checkImagePixels, IMAGE_TYPES, PROMO_KIND } from '../chat/ImageModals';

const TZ = 'America/Bogota';
const today = () => new Intl.DateTimeFormat('en-CA', { timeZone: TZ }).format(new Date());
function plusDays(day, n) {
  const d = new Date(`${day}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
const fmtDay = day => new Date(`${day}T12:00:00Z`).toLocaleDateString('es-CO', { timeZone: 'UTC', day: 'numeric', month: 'short', year: 'numeric' });

/**
 * Biblioteca de promociones (solo admin sube y borra; los asesores las envían
 * desde el chat). Evento: vence máx. a 30 días; informativa: máx. a 90. Se
 * borran solas al terminar el día del vencimiento. Máximo 20 vigentes.
 */
export default function PromotionsPanel() {
  const { apiFetch } = useApi();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      setData(await apiFetch('/api/promotions'));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [apiFetch]);
  useEffect(() => { load(); }, [load]);

  async function remove(p) {
    if (!window.confirm(`¿Borrar "${p.title}" de la biblioteca? Los chats donde se envió mostrarán "Imagen ya no disponible".`)) return;
    try {
      await apiFetch(`/api/admin/promotions/${p.id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      alert(e.message);
    }
  }

  const full = data && data.active >= data.max;
  const soon = (data?.items || []).filter(p => p.expiring_soon);

  return (
    <div style={s.wrap}>
      <div style={s.headRow}>
        <h2 style={s.h2}>Biblioteca de promociones</h2>
        {data && (
          <span style={{ ...s.counter, ...(full ? s.counterFull : {}) }} aria-live="polite">
            {data.active} de {data.max} vigentes
          </span>
        )}
      </div>
      <p style={s.muted}>
        Imágenes que los asesores envían desde el chat (botón Promociones). Se borran solas al terminar el día del vencimiento.
        Evento: máximo {data?.max_days?.event ?? 30} días · Informativa (precios, ubicación, horarios): máximo {data?.max_days?.info ?? 90} días.
      </p>

      {soon.length > 0 && (
        <div style={s.warnBox} role="status">
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <span>
            {soon.length === 1 ? 'Una imagen vence' : `${soon.length} imágenes vencen`} en {data.warn_days} días o menos:{' '}
            {soon.map(p => `${p.title} (${p.days_left === 0 ? 'hoy' : `${p.days_left} d`})`).join(', ')}.
          </span>
        </div>
      )}

      <UploadForm full={full} onUploaded={load} apiFetch={apiFetch} maxDays={data?.max_days} />
      {error && <div style={s.errorBox}>⚠️ {error}</div>}

      <div style={s.grid}>
        {data?.items.length === 0 && <div style={s.muted}>No hay imágenes vigentes.</div>}
        {data?.items.map(p => (
          <div key={p.id} style={{ ...s.card, ...(p.expiring_soon ? s.cardSoon : {}) }}>
            <div style={s.thumb}><AuthImage url={p.image_url} alt={p.title} maxHeight={150} style={{ width: '100%', height: 150, objectFit: 'cover' }} /></div>
            <div style={s.cardTitle} title={p.title}>{p.title}</div>
            <div style={s.cardMeta}>
              <span style={s.kind}>{PROMO_KIND[p.kind]}</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: p.expiring_soon ? 'var(--warning)' : 'var(--text-3)' }}>
                <CalendarClock size={12} /> {fmtDay(p.expires_on)} · {p.days_left === 0 ? 'vence hoy' : `${p.days_left} d`}
              </span>
            </div>
            <div style={s.cardFoot}>
              <span style={s.muted}>{p.created_by_name || '—'}</span>
              <button style={s.dangerBtn} onClick={() => remove(p)} aria-label={`Borrar ${p.title}`}><Trash2 size={13} /> Borrar</button>
            </div>
          </div>
        ))}
      </div>

      <TemplateImages apiFetch={apiFetch} />
    </div>
  );
}

/**
 * Imagen fija de las plantillas de Meta con encabezado de imagen. Meta pide la
 * imagen en cada envío: se carga una vez aquí y el portal la adjunta al enviar.
 * No vence ni cuenta en el máximo de promociones.
 */
function TemplateImages({ apiFetch }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const all = await apiFetch('/api/templates');
      setList(all.filter(t => t.header?.format === 'IMAGE' && t.status !== 'DELETED'));
      setError('');
    } catch (e) {
      setError(e.message);
    }
  }, [apiFetch]);
  useEffect(() => { load(); }, [load]);

  return (
    <section style={{ ...s.wrap, marginTop: 18 }} aria-labelledby="tpl-img-title">
      <h2 id="tpl-img-title" style={s.h2}>Imágenes de plantillas de Meta</h2>
      <p style={s.muted}>
        Meta pide la imagen en cada envío de una plantilla con encabezado de imagen. Cárgala una vez aquí y el portal la
        adjunta sola cuando se envía la plantilla (Nueva conversación o Enviar plantilla). No vence ni cuenta en el máximo de promociones.
      </p>
      {error && <div style={s.errorBox}>⚠️ {error}</div>}
      {list?.length === 0 && <div style={s.muted}>No hay plantillas con encabezado de imagen. Si acabas de crearlas en Meta, sincroniza en Administración → Plantillas.</div>}
      <div style={s.grid}>
        {list?.map(t => <TemplateImageCard key={t.id} t={t} apiFetch={apiFetch} onChanged={load} />)}
      </div>
    </section>
  );
}

function TemplateImageCard({ t, apiFetch, onChanged }) {
  const fileRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function upload(file) {
    const fileError = checkImageFile(file) || (await checkImagePixels(file));
    if (fileError) return setError(fileError);
    setBusy(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      await apiFetch(`/api/templates/${t.id}/header-image`, { method: 'PUT', body: fd });
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  }

  async function remove() {
    if (!window.confirm(`¿Quitar la imagen de "${t.name}"? La plantilla no se podrá enviar hasta que cargues otra.`)) return;
    setBusy(true);
    try {
      await apiFetch(`/api/templates/${t.id}/header-image`, { method: 'DELETE' });
      onChanged();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  const approved = t.status === 'APPROVED';
  return (
    <div style={{ ...s.card, ...(t.header_image ? {} : s.cardSoon) }}>
      <div style={s.thumb}>
        {t.header_image
          ? <AuthImage url={t.header_image.url} alt={`Imagen de ${t.name}`} maxHeight={150} style={{ width: '100%', height: 150, objectFit: 'cover' }} />
          : <div style={s.noImage}><ImageOff size={22} /> Sin imagen: no se puede enviar</div>}
      </div>
      <div style={s.cardTitle} title={t.name}>{t.name}</div>
      <div style={s.cardMeta}>
        <span style={{ color: 'var(--text-3)' }}>{t.language} · {t.category}{approved ? '' : ` · ${t.status}`}</span>
        <span style={{ color: 'var(--text-2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={t.body.text}>{t.body.text}</span>
      </div>
      <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(',')} style={{ display: 'none' }}
        onChange={e => e.target.files?.[0] && upload(e.target.files[0])} aria-label={`Imagen para ${t.name}`} />
      <div style={s.cardFoot}>
        <button style={s.smallBtn} disabled={busy} onClick={() => fileRef.current?.click()}>
          {busy ? <Loader2 size={13} className="ui-spin" /> : <Upload size={13} />} {t.header_image ? 'Cambiar' : 'Cargar imagen'}
        </button>
        {t.header_image && (
          <button style={s.dangerBtn} disabled={busy} onClick={remove} aria-label={`Quitar la imagen de ${t.name}`}><Trash2 size={13} /> Quitar</button>
        )}
      </div>
      {error && <div style={{ ...s.errorBox, padding: '6px 10px', fontSize: 12 }}>⚠️ {error}</div>}
    </div>
  );
}

function UploadForm({ full, onUploaded, apiFetch, maxDays }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [kind, setKind] = useState('event');
  const [expires, setExpires] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const min = today();
  const max = plusDays(min, maxDays?.[kind] ?? (kind === 'event' ? 30 : 90));

  async function submit(e) {
    e.preventDefault();
    const fileError = checkImageFile(file);
    if (fileError) return setError(fileError);
    const pixelError = await checkImagePixels(file);
    if (pixelError) return setError(pixelError);
    if (!expires) return setError('La fecha de vencimiento es obligatoria');
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', title.trim());
      fd.append('kind', kind);
      fd.append('expires_on', expires);
      await apiFetch('/api/admin/promotions', { method: 'POST', body: fd });
      setFile(null); setTitle(''); setExpires('');
      if (fileRef.current) fileRef.current.value = '';
      onUploaded();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={submit} style={s.form}>
      <input ref={fileRef} type="file" accept={IMAGE_TYPES.join(',')} style={s.file}
        onChange={e => { setFile(e.target.files?.[0] || null); setError(''); }} aria-label="Imagen JPG o PNG" required />
      <input style={s.input} placeholder="Título (ej. Open house sábado)" value={title} minLength={2} maxLength={80}
        onChange={e => setTitle(e.target.value)} required />
      <select style={s.select} value={kind} onChange={e => { setKind(e.target.value); setExpires(''); }} aria-label="Tipo">
        <option value="event">{PROMO_KIND.event} (máx. {maxDays?.event ?? 30} días)</option>
        <option value="info">{PROMO_KIND.info} (máx. {maxDays?.info ?? 90} días)</option>
      </select>
      <label style={s.dateLabel}>
        Vence
        <input type="date" style={s.select} value={expires} min={min} max={max} onChange={e => setExpires(e.target.value)} required />
      </label>
      <button type="submit" className="ui-primary" style={s.primaryBtn} disabled={saving || full}
        title={full ? 'Ya hay el máximo de imágenes vigentes: borra una primero' : undefined}>
        {saving ? <Loader2 size={14} className="ui-spin" /> : <Upload size={14} />} {full ? 'Biblioteca llena' : 'Subir'}
      </button>
      {error && <div style={{ ...s.errorBox, width: '100%' }}>⚠️ {error}</div>}
    </form>
  );
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 14 },
  headRow: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  h2: { fontSize: 16, fontWeight: 800, margin: 0, color: 'var(--text)' },
  counter: { fontSize: 12.5, fontWeight: 700, padding: '4px 10px', borderRadius: 20, background: 'var(--accent-soft)', border: '1px solid var(--accent-border)', color: 'var(--text)' },
  counterFull: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', color: 'var(--danger)' },
  muted: { color: 'var(--text-3)', fontSize: 12.5, margin: 0 },
  warnBox: { display: 'flex', gap: 8, alignItems: 'flex-start', background: 'var(--warning-soft, var(--surface-2))', border: '1px solid var(--warning)', borderRadius: 10, padding: '10px 12px', fontSize: 13, color: 'var(--text)' },
  form: { display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 14 },
  file: { fontSize: 12.5, color: 'var(--text-2)', maxWidth: 240 },
  input: { background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', color: 'var(--text)', fontSize: 13, minWidth: 220, flex: 1 },
  select: { background: 'var(--raised)', border: '1px solid var(--border)', borderRadius: 8, padding: '7px 10px', color: 'var(--text)', fontSize: 13 },
  dateLabel: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-2)' },
  primaryBtn: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--accent)', border: 'none', borderRadius: 10, padding: '9px 16px', color: 'var(--on-accent)', fontWeight: 700, fontSize: 13, cursor: 'pointer' },
  errorBox: { background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 10, padding: '10px 14px', color: 'var(--danger)', fontSize: 13 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 },
  card: { display: 'flex', flexDirection: 'column', gap: 6, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 12, padding: 8 },
  cardSoon: { borderColor: 'var(--warning)' },
  thumb: { borderRadius: 8, overflow: 'hidden', background: 'var(--surface-3)', height: 150 },
  cardTitle: { fontSize: 13.5, fontWeight: 700, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardMeta: { display: 'flex', flexDirection: 'column', gap: 3, fontSize: 12 },
  kind: { alignSelf: 'flex-start', fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20, background: 'var(--surface-3)', color: 'var(--text-2)' },
  cardFoot: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6, marginTop: 2 },
  noImage: { height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 6, color: 'var(--warning)', fontSize: 12, fontWeight: 600, textAlign: 'center', padding: 8 },
  smallBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--accent-soft)', border: '1px solid var(--accent-border)', borderRadius: 6, padding: '4px 8px', color: 'var(--text)', cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  dangerBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'var(--danger-soft)', border: '1px solid var(--danger-border)', borderRadius: 6, padding: '4px 8px', color: 'var(--danger)', cursor: 'pointer', fontSize: 12 },
};
